/**
 * ContractSlice Core Reducers
 * 
 * Core CRUD operations for contract management.
 */

import { PayloadAction } from '@reduxjs/toolkit';
import { ContractSliceState } from '../state';
import { ContractView, FeeScheduleView } from '../../types/contractTypes';
import { createDefaultContract } from '../../createDefaultContract';

/**
 * Set the current contract
 */
export const setSelectedContract = (
  state: ContractSliceState,
  action: PayloadAction<ContractView | null>
) => {
  state.currentContract = action.payload;
  state.isDirty = false;
  state.selectedContractTabIndex = 0;
};

/**
 * Set the current fee schedule
 */
export const setSelectedFeeSchedule = (
  state: ContractSliceState,
  action: PayloadAction<FeeScheduleView | null>
) => {
  state.currentFeeSchedule = action.payload;
  state.isDirty = false;
};

/**
 * Set whether user is in edit mode
 */
export const setIsEditing = (
  state: ContractSliceState,
  action: PayloadAction<boolean>
) => {
  state.isEditing = action.payload;
  if (!action.payload) {
    state.isDirty = false;
  }
};

/**
 * Set whether there are unsaved changes
 */
export const setIsDirty = (
  state: ContractSliceState,
  action: PayloadAction<boolean>
) => {
  state.isDirty = action.payload;
};

/**
 * Update current contract with partial data
 */
export const updateContract = (
  state: ContractSliceState,
  action: PayloadAction<Partial<ContractView>>
) => {
  if (state.currentContract) {
    state.currentContract = { ...state.currentContract, ...action.payload };
    state.isDirty = true;
  }
};

/**
 * Update current fee schedule with partial data
 */
export const updateFeeSchedule = (
  state: ContractSliceState,
  action: PayloadAction<Partial<FeeScheduleView>>
) => {
  if (state.currentFeeSchedule) {
    state.currentFeeSchedule = { ...state.currentFeeSchedule, ...action.payload };
    state.isDirty = true;
  }
};

/**
 * Clear the current contract and fee schedule
 */
export const clearContract = (state: ContractSliceState) => {
  state.currentContract = null;
  state.currentFeeSchedule = null;
  state.isEditing = false;
  state.isDirty = false;
  state.validationErrors = {};
};

/**
 * Initialize a new contract with defaults
 */
export const initializeContract = (
  state: ContractSliceState,
  action: PayloadAction<{ company: string }>
) => {
  const defaultContract = createDefaultContract();
  defaultContract.company = action.payload.company;
  defaultContract.effectiveDate = new Date().toISOString();

  state.currentContract = defaultContract;
  state.isEditing = true;
  state.isDirty = true;
};

/**
 * Set the selected tab index
 */
export const setSelectedContractTabIndex = (
  state: ContractSliceState,
  action: PayloadAction<number>
) => {
  state.selectedContractTabIndex = action.payload;
};

/**
 * Add a center to the current contract
 */
export const addCenterToContract = (
  state: ContractSliceState,
  action: PayloadAction<{ centerId: string; centerCode: string; centerName: string }>
) => {
  if (state.currentContract) {
    const newCenter = {
      code: action.payload.centerCode,
      name: action.payload.centerName,
    };
    // Add only if not already exists
    const exists = state.currentContract.centers.some(
      (center) => center.code === action.payload.centerCode
    );
    if (!exists) {
      state.currentContract.centers.push(newCenter);
      state.isDirty = true;
    }
  }
};

/**
 * Remove a center from the current contract
 */
export const removeCenterFromContract = (
  state: ContractSliceState,
  action: PayloadAction<string>
) => {
  if (state.currentContract) {
    state.currentContract.centers = state.currentContract.centers.filter(
      (center) => center.code !== action.payload
    );
    state.isDirty = true;
  }
};

/**
 * Update all centers for the current contract
 */
export const updateContractCenters = (
  state: ContractSliceState,
  action: PayloadAction<{ code: string; name: string }[]>
) => {
  if (state.currentContract) {
    state.currentContract.centers = action.payload;
    state.isDirty = true;
  }
};
